//   _____                          _   _   _
//  |  ___|__  _ __ _ __ ___   __ _| |_| |_(_)_ __   __ _
//  | |_ / _ \| '__| '_ ` _ \ / _` | __| __| | '_ \ / _` |
//  |  _| (_) | |  | | | | | | (_| | |_| |_| | | | | (_| |
//  |_|  \___/|_|  |_| |_| |_|\__,_|\__|\__|_|_| |_|\__, |
//                                                  |___/
import { parsePhoneNumberFromString as _parsePhoneNumberFromString } from 'libphonenumber-js';

import metadata from '../../assets/phone-number.metadata.json';

// Regular expressions
const CURRENCY_REGEX = /(\d)(?=(\d{3})+(?!\d))/g;
export const ADDRESS_REGEX = /^([#0-9A-Za-zÀ-ÖØ-öø-ÿ '-])+$/g;
export const AVAILABLE_STATES = /^((?!IA)(?!MD)(?!WV).)*$/;
export const DATE_REGEX =
  /^(0[1-9]|10|11|12)\/(0[1-9]|1[0-9]|2[0-9]|30|31)\/1[0-9]{3}|20[0-9]{2}/;
export const DIGITS_REGEX = /\d/g;
export const DIGITS_INCLUDE_X_REGEX = /[x0-9]/g;
export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const MAX_REGEX = /^\d{1,3}(,\d{3}){0,2}$|1,000,000,000$/;
export const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ '-]+$/g;
export const NON_DIGITS_REGEX = /\D/g;
export const NOT_PO_BOX_REGEX =
  /^(?!p\.?\s?-?\/?o\.?\s?-?(b\.|b\s|box)|post\sbox|post\soffice|box(\s?\d|-\d|\s?#)).*$/i;
export const PHONE_NUMBER_REGEX = /^(?!0+$)\d{8,}$/g;
export const SSN_REGEX = /^[0-8][0-9]{2}?(?!00)[0-9]{2}?(?!0000)[0-9]{4}/;
export const UUID_REGEX =
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
export const ZIP_REGEX = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
export const NON_DIGITS_EXCEPT_X_REGEX = /[^\dx]/g;

// CURRENCY ---------------------------------------------------------------------------------------
// Formats incoming text with commas to look like a currency value - note: no dollar sign is added
export const formatCurrencyInputWithCommas = (incomingCurrency: string) => {
  if (incomingCurrency.length === 0) return '';
  const currencyDigitsOnly: string = incomingCurrency.replace(
    NON_DIGITS_REGEX,
    ''
  );
  let currencyTrimmed: string = '';
  if (currencyDigitsOnly.length === 0) {
    currencyTrimmed = '';
  } else {
    currencyTrimmed = parseInt(currencyDigitsOnly, 10).toString();
  }
  const formatted = currencyTrimmed.replace(CURRENCY_REGEX, '$1,');
  return formatted;
};

export const formatNumberWithCommas = (incomingNumber: string) =>
  incomingNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// ZIP --------------------------------------------------------------------------------------------
// Formats incoming text (if longer than 5 digits) to 9 digit zipcode
export const formatZip = (incomingZip: string) => {
  const zipCodeDigitsOnly = incomingZip.replace(NON_DIGITS_REGEX, '');
  const formatted =
    zipCodeDigitsOnly.length > 5
      ? [
          ...zipCodeDigitsOnly.split('').slice(0, 5),
          '-',
          ...zipCodeDigitsOnly.split('').slice(5),
        ].join('')
      : zipCodeDigitsOnly;
  return formatted;
};

// DATE -------------------------------------------------------------------------------------------
// formats incoming text to look like a date with template MM/DD/YYYY
export const formatDate = (incomingDate: string) => {
  // strip out everything but digits
  const dateDigitsOnly = incomingDate.replace(NON_DIGITS_REGEX, '');
  // get rid of any digits added beyond eight
  const trimmedDate =
    dateDigitsOnly.length <= 8 ? dateDigitsOnly : dateDigitsOnly.slice(0, 8);
  // if there are only two digits, just return them as-is
  if (trimmedDate.length <= 2) return trimmedDate;
  // More than two, less than five, insert the one slash
  if (trimmedDate.length > 2 && trimmedDate.length < 5)
    return `${trimmedDate.slice(0, 2)}/${trimmedDate.slice(2)}`;
  // five or more, insert two slashes
  return `${trimmedDate.slice(0, 2)}/${trimmedDate.slice(2, 4)}/${trimmedDate.slice(4)}`;
};

// PHONE NUMBER -----------------------------------------------------------------------------------
// formats incoming text to look like a phone number
export const formatPhoneNumber = (incomingPhoneNumber: string) => {
  // strip out everything but digits
  const phoneNumberDigitsOnly = incomingPhoneNumber.replace(
    NON_DIGITS_REGEX,
    ''
  );
  // get rid of any digits added beyond ten
  const trimmedPhoneNumberWithoutLeadingOne =
    phoneNumberDigitsOnly.length > 10 && phoneNumberDigitsOnly[0] === '1'
      ? phoneNumberDigitsOnly.slice(1, 11)
      : phoneNumberDigitsOnly;
  const trimmedPhoneNumber =
    trimmedPhoneNumberWithoutLeadingOne.length <= 10
      ? trimmedPhoneNumberWithoutLeadingOne
      : trimmedPhoneNumberWithoutLeadingOne.slice(0, 10);
  // if there are only 3 digits, just return them as-is
  if (trimmedPhoneNumber.length <= 3) return trimmedPhoneNumber;
  // More than three, less than seven, insert the one hyphen
  if (trimmedPhoneNumber.length <= 6)
    return `${trimmedPhoneNumber.slice(0, 3)}-${trimmedPhoneNumber.slice(3, trimmedPhoneNumber.length)}`;
  // seven or more, insert two hyphens
  return `${trimmedPhoneNumber.slice(0, 3)}-${trimmedPhoneNumber.slice(3, 6)}-${trimmedPhoneNumber.slice(6, trimmedPhoneNumber.length)}`;
};

function call(this: any, func: any, _arguments: any) {
  const args = Array.prototype.slice.call(_arguments);
  args.push(metadata);
  return func.apply(this, args);
}

export function parsePhoneNumberFromString(...args: any[]) {
  return call(_parsePhoneNumberFromString, args);
}

export const disallowNonNumeric = (formatted: string) =>
  (formatted || '').replace(/\D/g, '');

export const isValidPhoneNumber = (phoneNumber: string = ''): boolean => {
  const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, 'US');
  const formattedPhoneNumber = disallowNonNumeric(phoneNumber);
  if (parsedPhoneNumber && formattedPhoneNumber.length === 10) {
    return (
      parsedPhoneNumber.isValid() &&
      (window.location.hostname !== 'www.prosper.com' ||
        parsedPhoneNumber.getType() !== 'TOLL_FREE')
    );
  }
  return false;
};

// formatting before sending to GQL
export const finalizeDateOfBirth = (unformattedDateOfBirth: string) => {
  // unformatted: MM/DD/YYYY
  // BE expects: YYYY-MM-DD
  const formattedDateOfBirth = `${unformattedDateOfBirth.slice(6, 10)}-${unformattedDateOfBirth.slice(0, 2)}-${unformattedDateOfBirth.slice(3, 5)}`;
  return formattedDateOfBirth;
};

export const finalizeCurrency = (unformattedCurrency: string) =>
  unformattedCurrency.replace(NON_DIGITS_REGEX, '');

export const finalizeOptionalCurrency = (
  unformattedCurrency: string
): string | null => {
  const formattedCurrency = unformattedCurrency.replace(NON_DIGITS_REGEX, '');
  return formattedCurrency.length > 0 ? formattedCurrency : null;
};

// formats Date object into "MMM DD, YYYY"
export const formatDocumentDate = (dateObj: Date) => {
  const formattedMonth = new Intl.DateTimeFormat('en', {
    month: 'short',
  }).format(dateObj);
  const formattedDay = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(
    dateObj
  );
  const formattedYear = new Intl.DateTimeFormat('en', {
    year: 'numeric',
  }).format(dateObj);
  return `${formattedMonth} ${formattedDay}, ${formattedYear}`;
};

export const capitalizeFirstName = (name: string) =>
  `${name.charAt(0).toUpperCase()}${name.slice(1)}`;

// format names to possessives: Alex -> Alex's
export const formatPossessive = (name: string) => {
  const properCaseName = capitalizeFirstName(name);
  // check if name ends in 's'
  // include uppercase 'S' for edge case where first name is just the letter 'S'
  const sMap = ['s', 'S'];
  if (sMap.includes(name.charAt(name.length - 1))) {
    return `${properCaseName}'`;
  }
  return `${properCaseName}'s`;
};
